import { useState } from 'react';
import { Save } from 'lucide-react';
import { toast } from 'sonner';

import { productionApi } from 'api/production';
import { CONTENT_VARIANTS } from 'components/production/components/AisleContent/constants';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import BannerCarousel1Form, { MIN_BANNER_ITEMS as MIN_CAROUSEL_1_ITEMS } from 'shared/components/BannerCarousel1Form';
import BannerCarousel2Form, { MIN_BANNER_ITEMS as MIN_CAROUSEL_2_ITEMS } from 'shared/components/BannerCarousel2Form';
import BannerCarousel3Form, { MIN_BANNER_ITEMS as MIN_CAROUSEL_3_ITEMS } from 'shared/components/BannerCarousel3Form';

import BannerEditorActions from '../BannerEditor/BannerEditorActions';
import { useEditorMutation } from '../hooks/useEditorMutation';

import BannerCarouselEditorSummary from './BannerCarouselEditorSummary';
import { buildBannerCarouselPayload, transformBannerCarouselToFormData } from './utils';

const FORM_BY_DISPLAY_TYPE = {
    BANNER_CAROUSEL_1: BannerCarousel1Form,
    BANNER_CAROUSEL_2: BannerCarousel2Form,
    BANNER_CAROUSEL_3: BannerCarousel3Form,
};

const MIN_ITEMS_BY_DISPLAY_TYPE = {
    BANNER_CAROUSEL_1: MIN_CAROUSEL_1_ITEMS,
    BANNER_CAROUSEL_2: MIN_CAROUSEL_2_ITEMS,
    BANNER_CAROUSEL_3: MIN_CAROUSEL_3_ITEMS,
};

/**
 * Displays an editor for banner carousels (supports Banner Carousel 1, 2, and 3).
 *
 * @param {string} aisleID - Parent aisle ID.
 * @param {Object} banner - Banner carousel data.
 * @param {Function} handleDeleteNewEditor - Callback function to delete a new banner editor.
 * @param {string} initialView - Initial view, defaults to 'summary'.
 * @param {Function} refetchBanners - Function to refetch banners from the parent component.
 */
export default function BannerCarouselEditor({
    aisleID,
    banner,
    handleDeleteNewEditor,
    initialView = 'summary',
    refetchBanners,
}) {
    const [view, setView] = useState(initialView);
    const [formData, setFormData] = useState(() => (banner.id ? transformBannerCarouselToFormData(banner) : banner));
    const { executeMutation, isSaving } = useEditorMutation({
        entityName: 'Banner carousel',
        refetch: refetchBanners,
    });

    const variantName = CONTENT_VARIANTS[formData.displayType]?.name ?? 'Banner Carousel';
    const FormComponent = FORM_BY_DISPLAY_TYPE[formData.displayType] ?? BannerCarousel1Form;
    const minItems = MIN_ITEMS_BY_DISPLAY_TYPE[formData.displayType] ?? MIN_CAROUSEL_1_ITEMS;
    const hasEnoughItems = formData.items.length >= minItems;

    const toggleView = () => {
        setView((prev) => (prev === 'summary' ? 'form' : 'summary'));
    };

    const handleFormChange = (updatedData) => {
        setFormData(updatedData);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (formData.eyebrow && !formData.heading) {
            toast.error('A heading is required when an eyebrow is provided.');
            return;
        }
        await executeMutation({
            action: async () => {
                if (banner.id) {
                    const response = await productionApi.updateAisleBanner({
                        id: banner.id,
                        ...buildBannerCarouselPayload(aisleID, formData),
                    });
                    if (!response.status) {
                        throw new Error('Failed to update banner carousel');
                    }
                } else {
                    const response = await productionApi.createAisleBanner(
                        buildBannerCarouselPayload(aisleID, formData),
                    );
                    if (!response.status) {
                        throw new Error('Failed to create banner carousel');
                    }
                    handleDeleteNewEditor?.();
                }
            },
        });
    };

    const handleDelete = async () => {
        if (!banner.id) {
            handleDeleteNewEditor();
            return;
        }
        await executeMutation({
            action: async () => {
                const response = await productionApi.deleteAisleBanner(banner.id);
                if (!response.status) {
                    throw new Error(response.msg || 'Failed to delete banner carousel');
                }
            },
            loading: 'Deleting banner carousel...',
            success: 'Banner carousel deleted successfully',
        });
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center justify-between">
                    {banner.bannerName ? (
                        <div className="flex items-center gap-1">
                            <h3>{banner.bannerName}</h3>
                            <span className="ml-2 text-sm text-muted-foreground">({variantName})</span>
                            <span className="ml-2 font-normal text-sm text-muted-foreground">id: {banner.id}</span>
                        </div>
                    ) : (
                        <h3>{variantName}</h3>
                    )}
                    <BannerEditorActions onDelete={handleDelete} toggleView={toggleView} view={view} />
                </CardTitle>
            </CardHeader>
            <CardContent>
                {view === 'summary' && <BannerCarouselEditorSummary data={banner} formData={formData} />}
                {view === 'form' && (
                    <form className="flex flex-col gap-6" onSubmit={handleSave}>
                        <FormComponent data={formData} disabled={isSaving} onChange={handleFormChange} />
                        <Button className="self-end w-content" disabled={isSaving || !hasEnoughItems}>
                            <Save />
                            Save changes
                        </Button>
                    </form>
                )}
            </CardContent>
        </Card>
    );
}
